import axios from 'axios';
import * as z from 'zod';

export interface PagingRequestParams {
  skip?: number;
  top?: number;
  orderby?: string;
  filter?: string;
}

export interface PagingResponse<T> {
  pageStart: number;
  pageSize: number;
  totalCount: number;
  count: number;
  data: T[];
}

export interface PagingDataSource<T> {
  pageSize: number;

  load(pageNumber?: number): Promise<PagingResponse<T>>;
}

export const PagingDataSource = z.object({
  pageSize: z.number(),
  load: z.function()
});

export class CollectionBasedPagingDataSource<T> implements PagingDataSource<T> {
  items: Array<T>;
  pageSize: number;

  constructor(items: Array<T>, pageSize: number) {
    this.items = items;
    this.pageSize = pageSize;
  }

  load(pageNumber?: number | undefined): Promise<PagingResponse<T>> {
    pageNumber = pageNumber || 1;
    const size = this.pageSize;
    const startIndex = (pageNumber - 1) * size;

    const data = this.items.slice(startIndex, startIndex + size);
    return Promise.resolve({
      pageStart: startIndex,
      pageSize: this.pageSize,
      totalCount: this.items.length,
      count: data.length,
      data
    });
  }
}

export class Pager {
  private _items: Array<any>;

  perPage: number;
  history: Array<number>;
  currentPage: number;
  items!: Array<any>;
  startIndex!: number;

  constructor(items: Array<any>, pageSize: number) {
    this._items = items;
    this.perPage = pageSize;
    this.currentPage = 1;
    this.history = [];
    this.setCurrentPage(this.currentPage);
  }

  get totalRows() {
    return this._items.length;
  }

  setCurrentPage(page: number) {
    if (
      !this.history.length ||
      this.history[this.history.length - 1] != this.currentPage
    ) {
      this.history.push(this.currentPage);
    }

    const minPage = 1;
    const maxPage = Math.floor(this.totalRows / this.perPage) + 1;
    page = Math.min(Math.max(minPage, page), maxPage);

    const startIndex = (page - 1) * this.perPage;
    this.items = this._items.slice(startIndex, startIndex + this.perPage);
    this.currentPage = page;
    this.startIndex = startIndex;
  }

  setItems(items: Array<any>) {
    this._items = items;
    this.setCurrentPage(this.currentPage);
  }
}
